export const getNotRequested = (): NotRequested => ({
  kind: 'not-requested',
});

export const getRequesting = (): Requesting => ({
  kind: 'requesting',
});

export const getSuccess = <T>(data: T): Success<T> => ({
  data,
  kind: 'success',
});

export const getFailed = <E>(error: E): Failed<E> => ({
  error,
  kind: 'failed',
});

export const getRefreshing = <T, E>(prevData: Refreshable<T, E>): Refreshing<T, E> => ({
  kind: 'refreshing',
  prevData,
});

// PrevData would be a refreshable
export const getRefreshSuccess = <T, E>(
  prevData: Refreshable<T, E>,
  data: T,
): RefreshSuccess<T, E> => ({
  data,
  kind: 'refresh-success',
  prevData,
});

export const getRefreshFailed = <T, E>(
  prevData: Refreshable<T, E>,
  error: E,
): RefreshFailed<T, E> => ({
  error,
  kind: 'refresh-failed',
  prevData,
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NotRequestedHandler = () => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequestingHandler = () => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SuccessHandler<T> = (data: T) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FailedHandler<E> = (error: E) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RefreshHandler<T, E> = (prevData: Refreshable<T, E>) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RefreshSuccessHandler<T, E> = (prevData: Refreshable<T, E>, newData: T) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RefreshFailedHandler<T, E> = (prevData: Refreshable<T, E>, error: E) => any;

export const handleRequestable =
  <T, E>(
    onNotRequested: NotRequestedHandler,
    onRequesting: RequestingHandler,
    onSuccess: SuccessHandler<T>,
    onFailed: FailedHandler<E>,
  ) =>
  (
    resource: Requestable<T, E>,
  ): ReturnType<FailedHandler<E> | NotRequestedHandler | RequestingHandler | SuccessHandler<T>> => {
    switch (resource.kind) {
      case 'not-requested':
        return onNotRequested();
      case 'requesting':
        return onRequesting();
      case 'success':
        return onSuccess(resource.data);
      case 'failed':
        return onFailed(resource.error);
    }
  };

export const extractRequestable = <T, E>(resource: Requestable<T, E>): Maybe<T> =>
  resource.kind === 'success' ? resource.data : null;

export const extractRequestableError = <T, E>(resource: Requestable<T, E>): Maybe<E> =>
  resource.kind === 'failed' ? resource.error : null;

export const handleRefreshable =
  <T, E>(
    onNotRequested: NotRequestedHandler,
    onRefresh: RefreshHandler<T, E>,
    onSuccess: RefreshSuccessHandler<T, E>,
    onFailed: RefreshFailedHandler<T, E>,
  ) =>
  (
    resource: Refreshable<T, E>,
  ): ReturnType<
    | NotRequestedHandler
    | RefreshFailedHandler<T, E>
    | RefreshHandler<T, E>
    | RefreshSuccessHandler<T, E>
  > => {
    switch (resource.kind) {
      case 'not-requested':
        return onNotRequested();
      case 'refreshing':
        return onRefresh(resource.prevData);
      case 'refresh-success':
        return onSuccess(resource.prevData, resource.data);
      case 'refresh-failed':
        return onFailed(resource.prevData, resource.error);
    }
  };

export const extractRefreshable = <T, E>(resource: Refreshable<T, E>): Maybe<T> =>
  handleRefreshable<T, E>(
    () => null,
    (prevData) => (prevData.kind !== 'not-requested' ? extractRefreshable<T, E>(prevData) : null),
    (_, data) => data,
    () => null,
  )(resource);

// extract the current value from the Refreshable as above, but in the case of failure, return the last known value
export const extractRefreshableLastValue = <T, E>(resource: Refreshable<T, E>): Maybe<T> =>
  handleRefreshable<T, E>(
    () => null,
    (prevData) =>
      prevData.kind !== 'not-requested' ? extractRefreshableLastValue<T, E>(prevData) : null,
    (_, data) => data,
    (prevData) =>
      prevData.kind !== 'not-requested' ? extractRefreshableLastValue<T, E>(prevData) : null,
  )(resource);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const requestableHasFailed = (requestable: Requestable<any, any>): boolean =>
  requestable.kind === 'failed';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const requestableHasSucceeded = (requestable: Requestable<any, any>): boolean =>
  requestable.kind === 'success';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const requestableIsLoading = (requestable: Requestable<any, any>): boolean =>
  requestable.kind === 'requesting';
